/**
 * Firmware Update Utilities
 *
 * Handles firmware binary file validation and version parsing.
 * Version is extracted from filename in format: *_v{major}.{minor}.{patch}.bin
 * Examples:
 *   - MasterModule_v1.2.0.bin
 *   - firmware_v2.0.1.bin
 *   - STAK_MIDI_v1.0.0.bin
 */

/** Parsed firmware version */
export interface FirmwareVersion {
  major: number;
  minor: number;
  patch: number;
  /** Version as hex: (major << 16) | (minor << 8) | patch */
  hex: number;
  /** Display string: "v1.2.0" */
  display: string;
}

/** Firmware file info after validation */
export interface FirmwareFileInfo {
  name: string;
  size: number;
  version: FirmwareVersion;
  data: Uint8Array;
}

/** Firmware status from device */
export interface FirmwareStatus {
  hasPendingUpdate: boolean;
  magic?: number;
  headerVersion?: number;
  size?: number;
  crc?: number;
  version?: FirmwareVersion;
}

/** Installed firmware info from device */
export interface InstalledFirmwareInfo {
  magic?: number;
  version?: FirmwareVersion;
  size?: number;
  crc?: number;
  installCount?: number;
}

/** Maximum firmware size (1MB = Bank 2 in dual bank mode) */
export const MAX_FIRMWARE_SIZE = 1024 * 1024;

/** Minimum firmware size (vector table at least) */
export const MIN_FIRMWARE_SIZE = 512;

/**
 * Filename pattern for version extraction
 * Matches: name_v1.2.3.bin or name_v1.2.3.bin
 * Groups: major, minor, patch
 */
const VERSION_PATTERN = /_v(\d+)\.(\d+)\.(\d+)\.bin$/i;

/**
 * Parse version from firmware filename
 * @throws Error if filename doesn't match expected pattern
 */
export function parseVersionFromFilename(filename: string): FirmwareVersion {
  const match = filename.match(VERSION_PATTERN);

  if (!match) {
    throw new Error(
      `Invalid firmware filename format: "${filename}"\n` +
        'Expected format: name_v{major}.{minor}.{patch}.bin\n' +
        'Examples: MasterModule_v1.2.0.bin, firmware_v2.0.1.bin'
    );
  }

  const major = parseInt(match[1]!, 10);
  const minor = parseInt(match[2]!, 10);
  const patch = parseInt(match[3]!, 10);

  // Validate ranges (each component must fit in 8 bits for hex encoding)
  if (major > 255 || minor > 255 || patch > 255) {
    throw new Error(
      `Version components must be 0-255. Got: ${major}.${minor}.${patch}`
    );
  }

  const hex = (major << 16) | (minor << 8) | patch;

  return {
    major,
    minor,
    patch,
    hex,
    display: `v${major}.${minor}.${patch}`,
  };
}

/**
 * Parse version from hex value (as returned by device)
 */
export function parseVersionFromHex(hex: number): FirmwareVersion {
  const major = (hex >> 16) & 0xff;
  const minor = (hex >> 8) & 0xff;
  const patch = hex & 0xff;

  return {
    major,
    minor,
    patch,
    hex,
    display: `v${major}.${minor}.${patch}`,
  };
}

/**
 * Format version hex for CLI command (e.g., "0x010200")
 */
export function formatVersionHex(version: FirmwareVersion): string {
  return `0x${version.hex.toString(16).padStart(6, '0')}`;
}

/**
 * Validate firmware binary file
 * @throws Error if file is invalid
 */
export function validateFirmwareBinary(data: Uint8Array): void {
  // Check size limits
  if (data.length < MIN_FIRMWARE_SIZE) {
    throw new Error(
      `Firmware too small: ${data.length} bytes (minimum: ${MIN_FIRMWARE_SIZE})`
    );
  }

  if (data.length > MAX_FIRMWARE_SIZE) {
    throw new Error(
      `Firmware too large: ${data.length} bytes (maximum: ${MAX_FIRMWARE_SIZE})`
    );
  }

  // Check for valid ARM Cortex-M vector table
  // First word is initial stack pointer (should be in RAM: 0x20xxxxxx or 0x24xxxxxx for STM32H7)
  // Second word is reset handler (should be in flash: 0x08xxxxxx, odd for Thumb)
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const stackPointer = view.getUint32(0, true); // Little-endian
  const resetHandler = view.getUint32(4, true);

  // Validate stack pointer is in STM32H7 RAM range
  const validRamRanges: Array<[number, number]> = [
    [0x20000000, 0x20020000], // DTCMRAM (128KB)
    [0x24000000, 0x24080000], // AXI SRAM (512KB)
    [0x30000000, 0x30048000], // D2 SRAM (288KB)
    [0x38000000, 0x38010000], // D3 SRAM (64KB)
  ];

  const spValid = validRamRanges.some(
    (range) => stackPointer >= range[0] && stackPointer <= range[1]
  );

  if (!spValid) {
    throw new Error(
      `Invalid firmware: Stack pointer 0x${stackPointer.toString(16)} not in valid RAM range`
    );
  }

  // Validate reset handler is in application flash area and is Thumb code (LSB = 1)
  // Application is in Bank 2 (dual bank mode): 0x08100000 - 0x081FFFFF
  const APP_START = 0x08100000;
  const APP_END = 0x08200000;

  if (resetHandler < APP_START || resetHandler >= APP_END) {
    throw new Error(
      `Invalid firmware: Reset handler 0x${resetHandler.toString(16)} not in application flash area`
    );
  }

  if ((resetHandler & 1) === 0) {
    throw new Error(
      `Invalid firmware: Reset handler 0x${resetHandler.toString(16)} is not Thumb code (LSB must be 1)`
    );
  }
}

/**
 * Read and validate firmware file
 * @returns Validated firmware info with binary data
 */
export async function readFirmwareFile(file: File): Promise<FirmwareFileInfo> {
  // Parse version from filename first (fail fast)
  const version = parseVersionFromFilename(file.name);

  // Read file contents
  const arrayBuffer = await file.arrayBuffer();
  const data = new Uint8Array(arrayBuffer);

  // Validate binary contents
  validateFirmwareBinary(data);

  return {
    name: file.name,
    size: data.length,
    version,
    data,
  };
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
}
